import { decodeJwt } from 'jose'
import { useObservableEagerState } from 'observable-hooks'
import type { PropsWithChildren } from 'react'
import { useEffect, useRef, useState } from 'react'

import { AccessLevel, type AccessTokenPayload } from '@salsita-ai/platform-contracts/api'
import { useLogger } from '@salsita-ai/platform-project-sdk/react'
import { Loader } from '@salsita-ai/platform-project-sdk/ui'

import { configuratorInterface } from './systems/configurator-interface.js'
import { createAuthenticatedMetalworksApi, setQuoteId, setQuoteLineId } from './systems/metalworks-api.js'

/*
 * When `quoteId` and `quoteLineId` are provided in GET params, they need to be linked to `configurationId`.
 * This is done by storing them in local storage and it makes page reloads possible without losing
 * the quote details. If user clears local storage, even access token is lost. In that case,
 * completely new redirect from Daveweb is needed.
 */

const quoteIdParam = 'quoteId'
const quoteLineIdParam = 'lineId'

const getQuoteUrlParams = () => {
  const url = new URL(location.href)
  const quoteId = parseInt(url.searchParams.get(quoteIdParam) ?? '')
  const quoteLineId = parseInt(url.searchParams.get(quoteLineIdParam) ?? '')

  return {
    quoteId: isNaN(quoteId) ? null : quoteId,
    quoteLineId: isNaN(quoteLineId) ? null : quoteLineId,
  }
}

const clearQuoteUrlParams = (shouldClone = false) => {
  const url = new URL(location.href)
  url.searchParams.delete(quoteIdParam)
  url.searchParams.delete(quoteLineIdParam)

  if (shouldClone) {
    url.searchParams.set('clone', '')
  }

  history.replaceState(null, '', url.toString())
}

export const clearCloneUrlParam = () => {
  const url = new URL(location.href)
  url.searchParams.delete('clone')
  history.replaceState(null, '', url.toString())
}

const shouldCloneConfiguration = (accessToken: string) => {
  const parsedToken = decodeJwt<AccessTokenPayload>(accessToken)
  return parsedToken.access === AccessLevel.Owner
}

/*
 * Resolve existing Daveweb quote lines after the SDK session is authenticated but
 * before the configurator starts loading configuration data. This keeps all
 * authentication and refresh operations on the SDK-provided SessionService.
 */
export const MetalworksSessionReadyProvider = (props: PropsWithChildren) => {
  const { children } = props
  const session = configuratorInterface.useSession()
  const routing = configuratorInterface.useConfiguratorRouting()
  const logger = useLogger()

  const quoteParamsRef = useRef(getQuoteUrlParams())
  const initializationRef = useRef<Promise<void> | null>(null)
  const requiresInitialization = quoteParamsRef.current.quoteId !== null && quoteParamsRef.current.quoteLineId !== null
  const [isReady, setIsReady] = useState(!requiresInitialization)
  const [initializationError, setInitializationError] = useState<Error | null>(null)

  useEffect(() => {
    if (!requiresInitialization) {
      return
    }

    const { quoteId, quoteLineId } = quoteParamsRef.current
    if (quoteId === null || quoteLineId === null) {
      return
    }

    if (initializationRef.current === null) {
      initializationRef.current = (async () => {
        const metalworksApi = createAuthenticatedMetalworksApi(session)
        const { configurationId, configurationToken } = await metalworksApi.getQuoteLine({ quoteId, quoteLineId })

        setQuoteId(configurationId, quoteId)
        setQuoteLineId(configurationId, quoteLineId)

        // Don't edit existing configurations loaded from Daveweb. Clone owner configurations instead.
        // Otherwise the state in Daveweb and in the configurator could get out of sync.
        const shouldClone = shouldCloneConfiguration(configurationToken)

        // This updates both the routing state and the SDK access-token storage. The routing service
        // is already mounted at this point, so changing only the URL would leave its state stale.
        routing.goToConfigurationDetail(configurationId, configurationToken)
        clearQuoteUrlParams(shouldClone)
      })()
    }

    let active = true
    void initializationRef.current.then(
      () => {
        if (active) {
          setIsReady(true)
        }
      },
      error => {
        if (!active) {
          return
        }

        const normalizedError = error instanceof Error ? error : new Error(`Unknown error ${String(error)}`)
        logger.error('Error fetching configuration ID', {}, normalizedError)
        window.alert('Could not load saved configuration. Please try again.')
        setInitializationError(normalizedError)
      }
    )

    return () => {
      active = false
    }
  }, [logger, requiresInitialization, routing, session])

  if (initializationError !== null) {
    throw initializationError
  }

  return isReady ? <>{children}</> : <Loader />
}

/*
 * This wrapper takes care of linking new configurations to existing quotes in Daveweb.
 * This happens when Daveweb redirects from a quote to add new line to it.
 */
export const MetalworksInitWrapper = (props: PropsWithChildren) => {
  const { children } = props
  const routing = configuratorInterface.useConfiguratorRouting()

  const { activeRoute } = useObservableEagerState(routing.state$)
  if (activeRoute.route !== 'Configuration') {
    throw new Error('MetalworksInitWrapper must be used within a Configuration route')
  }

  const { configurationId } = activeRoute
  const { quoteId } = getQuoteUrlParams()

  useEffect(() => {
    if (configurationId !== null && quoteId !== null) {
      setQuoteId(configurationId, quoteId)
      clearQuoteUrlParams()
    }
  }, [configurationId, quoteId])

  return children
}
